const isArray = Array.isArray;

const isString = (value) => typeof value === 'string' || Object.prototype.toString.call(value) === '[object String]';

const keys = Object.keys;
const entries = Object.entries;
const assign = Object.assign;

const seemsPlainObject = (value) => typeof value === 'object' && !!value && !(value instanceof Function) && value.constructor === Object;

/**
 * Very simple throttle function, based on one proposed here:
 * https://stackoverflow.com/a/27078401
 *
 * @private
 *
 * @param {function} callback - the function to be throttled
 * @param {boolean|number} interval - when is true, it uses requestAnimationFrame, otherwise a timeout of <interval> milliseconds
 * @returns {function} the throttled function
 */
function throttle(callback, interval) {
	let pending = false;
	let late = null;
	const f = (...args) => {
		if (!pending) {
			callback.apply(this, args);
			pending = true;
			const reset = () => {
				pending = false;
				if (late) {
					const c = late;
					late = null;
					c();
				}
			};
			if (interval === true) {
				requestAnimationFrame(reset);
			} else {
				setTimeout(reset, interval);
			}
		} else {
			late = () => f.apply(this, args);
		}
	};
	return f;
}

/**
 * Cast basic type values to strings.
 *
 * @private
 *
 * @param {(string|number|boolean|Array|object|null)} value - the value to be cast
 * @returns {string} - the string representation of the given value
 */
function encode(value) {
	if (value === true) {
		return 'true';
	}
	if (value === false) {
		return 'false';
	}
	if (value === null) {
		return 'null';
	}
	if (Array.isArray(value) || (typeof value === 'object' && !!value)) {
		value = String(JSON.stringify(value));
	}
	return value;
}

/**
 * Try to cast a value expressed as string to a more suitable standard type. Inspired by old jquery
 * data attribute parsing
 *
 * @private
 *
 * @param {string} value - the string value to be converted
 * @returns {(string|number|boolean|Array|object|null)} - The value after the cast
 */
function decode(value) {
	if (value === 'true') {
		return true;
	}
	if (value === 'false') {
		return false;
	}
	if (value === 'null') {
		return null;
	}
	try {
		// Only convert to a number if it doesn't change the string
		if (+value + '' === value) {
			return +value;
		}
		if (/^(?:\{[\w\W]*\}|\[[\w\W]*\])$/.test(value)) {
			return JSON.parse(value);
		}
		return value;
	} catch (e) {
		return value;
	}
}

//! nuff/lazy-custom-elements - @author Federico Orru' - @license Artistic-2.0

const elementsConnectedOnce = new WeakSet();
const baseHtmlConstructors = {};

function getParentConstructor(tagName) {
	if (!(tagName in baseHtmlConstructors)) {
		baseHtmlConstructors[tagName] = document.createElement(tagName).constructor;
	}
	return baseHtmlConstructors[tagName];
}

const registry = window.customElements;

const defaultOptions = {
	lazyValue: 'lazy',
	autodefineRoot: document,
	autodefineRootMargin: '100% 0px 100% 0px',
	autodefineThreshold: 0,
	autodefineAttribute: 'data-autodefine',
	autodefineDefaultValue: 'lazy',
	autoloadRoot: document,
	autoloadRootMargin: '50% 0px 50% 0px',
	autoloadThreshold: 0,
	autoloadAttribute: 'data-autoload',
	loadMethod: 'load',
	firstConnectedCallbackMethod: 'firstConnectedCallback',
};

const defaultDefineOptions = {
	extends: null,
	selector: '',
};

/**
 * The LazyCustomElementRegistry class is a custom element registry for defining and upgrading
 * custom elements in a lazy manner.
 *
 */
class LazyCustomElementRegistry {
	/**
	 * Creates an instance of LazyCustomElementRegistry.
	 */
	constructor() {
		/** @private @type {?number} */
		this.checkTimeout = null;

		/** @private @type {WeakMap} */
		this.components = new WeakMap();

		/** @private @type {WeakMap} */
		this.observers = new WeakMap();

		/** @private @type {Map} */
		this.entries = new Map();

		/** @private @type {string} */
		this.selectors = '';

		/** @private @type {Set} */
		this.selectorsSet = new Set();

		/** @private @type {?MutationObserver} */
		this.domObserver = null;

		/** @private @type {object} */
		this.options = assign({}, defaultOptions);
	}

	/**
	 * Equivalent of customElements.define() but supporting async definition and additional
	 * features. Define a custom element or custom built-in element, potentially in a lazy way, by
	 * passing an async callback that resolve to the Class
	 *
	 * @param {string} name The name of the custom element being defined, or a partial name ending
	 * with a dash in case of late definition of custom built-in elements classes from a mixin, see
	 * options.extends description.
	 * @param {Function} constructor The constructor function or class for the custom element
	 * being defined, or an async function that will resolve to the constructor. For
	 * @param {object} [options={}] An optional object containing configuration options for the
	 * custom element being defined. It may override some of the global options.
	 * @param {(string|Array)} [options.extends] the tagName of the built-in element to be
	 * extended. It allows to specify an array of names or the string '*' supporting the late
	 * definition of the class from a mixin in the form: const ClassName = (Base) => class extends
	 * Base {}. In that case the element name needs to be defined in a partial form with a trailing
	 * dash like 'acme-my-element-'. The final name of the elements will be completed adding the
	 * built-in element name that are going to be extended, like: 'acme-my-element-button',
	 * 'acme-my-element-a' and so on.
	 * @param {string} [options.selector] an alternative selector to identify custom elements
	 * in the dom in order to observe them for lazy defining and lazy loading. By default it will
	 * use the component name or a data attribute selector [is] for custom built-in elements
	 * @param {string} [options.autodefineAttribute=data-autodefine] attribute name (usually a data-attribute) to
	 * control the autodefinition behavior on the defined custom elements. Valid attribute values are 'true' (will be
	 * defined as soon as it is intercepted in the DOM), 'lazy' or an alternative value defined in options.lazyValue
	 * (will be defined when one element of this type intercepts a defined area around the viewport) or 'false' (will
	 * not be defined automatically, it will require an upgrade call to force the definition)
	 * @param {string} [options.lazyValue=lazy] value used to define the lazy behavior (both for autodefine and
	 * autoload features)
	 * @param {(Document|HTMLElement)} [options.autodefineRoot=document] root used for the autodefine
	 * IntersectionObserver
	 * @param {string} [options.autodefineRootMargin=100% 0px 100% 0px] margin used for the autodefine
	 * IntersectionObserver. By default it has a 100% height margin both above and below the viewport
	 * @param {number} [options.autodefineThreshold=0] threshold used for the autodefine IntersectionObserver
	 * @param {string} [options.autodefineDefaultValue=lazy] the default value for the autodefine attribute
	 * @param {(Document|HTMLElement)} [options.autoloadRoot=document] root used for the autoload IntersectionObserver
	 * @param {string} [options.autoloadRootMargin=50% 0px 50% 0px] margin used for the autoload
	 * IntersectionObserver. By default it has a 50% height margin both above and below the viewport
	 * @param {number} [options.autoloadThreshold=0] threshold used for the autodefine IntersectionObserver
	 * @param {string} [options.autoloadAttribute=data-autoload] attribute name (usually a data-attribute) to
	 * control the autoloading behavior on the defined custom elements. Valid attribute values are 'true' (will call the
	 * load method as soon as it is intercepted in the DOM), 'lazy', or an alternative value defined in
	 * options.lazyValue (will call the load method when the element is intercepted in a defined area around the
	 * viewport). Any other value will be ignored. Differently from the autodefine, elements that want to subscribe to
	 * the autoloading feature needs to have this attribute set.
	 * @param {string} [options.loadMethod=load] Name of the method to be called when the load is triggered
	 * @param {?(string|boolean)} [options.firstConnectedCallbackMethod=firstConnectedCallback] if the custom
	 * element class contains this method, it will be called only the first time the elements are connected to the
	 * document instead of the connectedCallback() method, which instead will be called (if available) on every
	 * reconnection after the first one. An empty string or a falsy value can be passed to disable the feature when the
	 * class contains such a method but you don't want to subscribe to this automated behavior.
	 *
	 * @returns {LazyCustomElementRegistry} return itself to allow for chain calls
	 */
	define(name, constructor, options = {}) {
		if (!(constructor instanceof Function)) {
			throw new Error('Constructor must be a class or function');
		}
		// for multiple custom built-in elements defined from the same base mixin, the name needs to
		// end with a dash
		const isMulti = name.substring(name.length - 1) === '-';
		if (isMulti) {
			if (!options.extends) {
				throw new Error('Invalid name ' + name + '  or missing extends option');
			}
			// when the multiple parent elements are defined as an array, we repeat the definition
			// internally for each of them definition for custom built-in elements that can extend
			// any html element passes '*' as options.extends value
			if (isArray(options.extends)) {
				for (const ext of options.extends) {
					this.define(name + ext, constructor, assign({}, options, {extends: ext}));
				}
				return this;
			}
		}
		// if the constructor class is passed directly in a sync way, we define the element
		// immediately as the standard CustomElementRegistry would do, otherwise we postpone the
		// definition in a lazy way to be performed only when needed
		const constructorOpts =
			constructor.prototype instanceof Element
				? {constructor: constructor}
				: {getConstructor: constructor, constructor: null};
		options = assign({}, defaultDefineOptions, options, constructorOpts);
		if (options.constructor && !isMulti) {
			constructor = this.prepareConstructor(constructor, options);
			options.constructor = constructor;
			registry.define(name, constructor, options.extends ? {extends: options.extends} : undefined);
		}

		let selector = options.selector;
		if (!selector) {
			// default selector for custom elements is their tagName. for custom built-in elements it
			// is an attribute selector
			selector = options.extends ? `[is${isMulti ? '^' : ''}="${name}"]` : name;
		}
		// we need a single selector (without duplicates) in order to match all the custom elements
		// together
		this.selectorsSet.add(selector);
		this.selectors = [...this.selectorsSet].join(',');

		this.entries.set(name, options);

		// after the definition we check the dom to find and observe already present elements. This
		// is done in an asynchronous way in order to not slow down in case of multiple definitions
		// in a row
		this.scheduleCheck();

		// this method is chainable to shorten multiple definitions
		return this;
	}

	/**
	 * Async version of CustomElementRegistry.get method. Retrieve a previously defined constructor
	 * for a given custom element name. It resolves late definitions made from a mixin in the form:
	 * const ClassName = (Base) => class extends Base {}. It also implements (through subclassing)
	 * the logic to deal with a firstConnectedCallback() method when the feature is enabled and the
	 * method is available in the defined constructor.
	 *
	 * @param {string} name the custom element or custom built-in element name associated to the
	 * constructor to retrieve
	 * @returns {Promise<(Function|undefined)>} resolves to a previously defined constructor or to
	 * undefined
	 */
	async get(name) {
		// when it is already registered in the original CustomElementRegistry there is nothing else to do
		let constructor = registry.get(name);
		if (constructor) {
			return constructor;
		}
		// when it was not defined, return undefined as the original CustomElementRegistry
		const entry = this.getEntry(name);
		if (!entry) {
			return undefined;
		}
		if (!entry.constructor) {
			// the constructor has not been resolved yet
			const getConstructor = entry.getConstructor;
			constructor = await getConstructor();
			constructor = this.prepareConstructor(constructor, entry);
			entry.constructor = constructor;
		}
		return entry.constructor;
	}

	/**
	 * Async version of CustomElementRegistry.upgrade method. First it resolves all the lazy defined
	 * elements, then proceed to upgrade them.
	 *
	 * @param {Element} root the element to be upgraded, together with its descendants
	 */
	async upgrade(root) {
		const elements = this.extractElements([root]);
		await this.upgradeElements(elements);
		registry.upgrade(root);
	}

	/**
	 * Exact same feature as CustomElementRegistry.whenDefined.
	 *
	 * @param {string} name a custom element or custom built-in element name
	 * @returns {Promise} resolves when the custom element constructor has been defined
	 */
	whenDefined(name) {
		return registry.whenDefined(name);
	}

	/**
	 * Same feature provided by {@link LazyCustomElementRegistry#upgrade} but without forcing the
	 * definition of lazy defined descendants.
	 *
	 * @param {Element} element the element to be upgraded
	 */
	async upgradeElement(element) {
		await this.prepareElement(element);
	}

	/**
	 * Same feature provided by {@link LazyCustomElementRegistry#upgrade} but for a list of elements
	 * and without forcing the definition of lazy defined descendants.
	 *
	 * @param {(Array|NodeList)} elements the elements to be upgraded
	 */
	async upgradeElements(elements) {
		await Promise.all([...elements].map((element) => this.prepareElement(element)));
	}

	/**
	 * Async equivalent of node.querySelector() followed by forcing a definition of the eventually
	 * lazy defined custom element and an upgrade of the found element.
	 *
	 * @param {(Document|Element)} node the document or node to be queried
	 * @param {string} selectors a string containing one or more comma-separated selectors to match
	 * @returns {Promise<?Element>} returns a promise that resolves with the found element or null
	 */
	async querySelector(node, selectors) {
		if (!(node instanceof Document) && !(node instanceof Element)) {
			return null;
		}
		const element = node.querySelector(selectors);
		if (element) {
			await this.prepareElement(element);
		}
		return element;
	}

	/**
	 * Async equivalent of node.querySelectorAll() followed by forcing a definition of the
	 * eventually lazy defined custom elements and an upgrade of the found elements.
	 *
	 * @param {(Document|Element)} node the document or node to be queried
	 * @param {string} selectors a string containing one or more comma-separated selectors to match
	 * @returns {Promise<?NodeList>} returns a promise that resolves with the found elements
	 * NodeList or null in case the node is not an instance of Document or Element to be queried
	 */
	async querySelectorAll(node, selectors) {
		if (!(node instanceof Document) && !(node instanceof Element)) {
			return null;
		}
		const elements = node.querySelectorAll(selectors);
		if (elements && elements.length) {
			await this.upgradeElements(elements);
		}
		return elements;
	}

	/**
	 * It works exactly like document.createElement with a single additional feature to solve a
	 * weird thing. When the standard document.createElement is used to create a custom built-in
	 * element (passing an options object as a second parameter, with the "is" property filled), it
	 * set the is attribute in a way that is not properly queryable with the usual methods. Let's
	 * say you do something like:
	 *
	 * ``` javascript
	 * class MyCustomParagraph extends HTMLParagraphElement {}
	 * customElements.define('my-custom-paragraph', MyCustomParagraph, {extends: 'p'});
	 * const p = document.createElement('p', {is: 'my-custom-paragraph});
	 * console.log(p.matches('[is="my-custom-paragraph"]'));
	 * // unexpectedly it prints false
	 * // same if you attach the element in the dom and try to query it by "is" attribute, you will get no results
	 * ```
	 *
	 * So in those cases, this method forces a setAttribute call with the proper values that allows
	 * the element to be queryable as expected.
	 *
	 * @param {string} tagName the tagName of the Element to be created
	 * @param {(undefined|object)} [options] An optional object eventually containing the property
	 * is
	 * @param {string} [options.is] The tagName of a custom built-in element
	 * @returns {Element} the created Element
	 */
	createElement(tagName, options) {
		const element = document.createElement(tagName, options);
		if (options && 'is' in options) {
			element.setAttribute('is', options.is);
		}
		return element;
	}

	/**
	 * Sets / overwrites config options
	 *
	 * @param {object} options An object containing configuration options
	 * @param {string} [options.autodefineAttribute=data-autodefine] attribute name (usually a data-attribute) to
	 * control the autodefinition behavior on the defined custom elements. Valid attribute values are 'true' (will be
	 * defined as soon as it is intercepted in the DOM), 'lazy' or an alternative value defined in options.lazyValue
	 * (will be defined when one element of this type intercepts a defined area around the viewport) or 'false' (will
	 * not be defined automatically, it will require an upgrade call to force the definition)
	 * @param {string} [options.lazyValue=lazy] value used to define the lazy behavior (both for autodefine and
	 * autoload features)
	 * @param {(Document|HTMLElement)} [options.autodefineRoot=document] root used for the autodefine
	 * IntersectionObserver
	 * @param {string} [options.autodefineRootMargin=100% 0px 100% 0px] margin used for the autodefine
	 * IntersectionObserver. By default it has a 100% height margin both above and below the viewport
	 * @param {number} [options.autodefineThreshold=0] threshold used for the autodefine IntersectionObserver
	 * @param {string} [options.autodefineDefaultValue=lazy] the default value for the autodefine attribute
	 * @param {(Document|HTMLElement)} [options.autoloadRoot=document] root used for the autoload IntersectionObserver
	 * @param {string} [options.autoloadRootMargin=50% 0px 50% 0px] margin used for the autoload
	 * IntersectionObserver. By default it has a 50% height margin both above and below the viewport
	 * @param {number} [options.autoloadThreshold=0] threshold used for the autodefine IntersectionObserver
	 * @param {string} [options.autoloadAttribute=data-autoload] attribute name (usually a data-attribute) to
	 * control the autoloading behavior on the defined custom elements. Valid attribute values are 'true' (will call the
	 * load method as soon as it is intercepted in the DOM), 'lazy', or an alternative value defined in
	 * options.lazyValue (will call the load method when the element is intercepted in a defined area around the
	 * viewport). Any other value will be ignored. Differently from the autodefine, elements that want to subscribe to
	 * the autoloading feature needs to have this attribute set.
	 * @param {string} [options.loadMethod=load] Name of the method to be called when the load is triggered
	 * @param {?(string|boolean)} [options.firstConnectedCallbackMethod=firstConnectedCallback] if the custom
	 * element class contains this method, it will be called only the first time the elements are connected to the
	 * document instead of the connectedCallback() method, which instead will be called (if available) on every
	 * reconnection after the first one. An empty string or a falsy value can be passed to disable the feature when the
	 * class contains such a method but you don't want to subscribe to this automated behavior.
	 */
	setOptions(options) {
		assign(this.options, options);
	}

	/**
	 * returns a copy of an the configuration options, to avoid accidental modification outside of the registry's scope.
	 * @see {@link LazyCustomElementRegistry#setOptions} for the full list of the options.
	 *
	 * @returns {object} a copy of the configuration options of the `options` object.
	 */
	getOptions() {
		return assign({}, this.options);
	}

	/**
	 * returns a copy of an the default configuration options, to avoid accidental modification.
	 * @see {@link LazyCustomElementRegistry#setOptions} for the full list of the options.
	 *
	 * @returns {object} a copy of the default configuration options of the `options` object.
	 */
	getDefaultOptions() {
		return assign({}, defaultOptions);
	}

	/**
	 * Prepares a custom element in case it was not already done before: it initializes
	 * IntersectionObservers for lazy definition and lazy loading, when needed and upgrade the
	 * element after their definition.
	 *
	 * @private
	 *
	 * @param {Element} element the element to be prepared
	 * @param {boolean} [force=true] when the element is discovered in the dom by the internal
	 * MutationObserver, the value is false, since we don't want to force the definition of lazy
	 * defined elements. In all the other cases, like calling upgrade() or querySelector, the value
	 * will be true.
	 */
	async prepareElement(element, force = true) {
		// we need to be sure the element is a real Element
		if (!element || !(element instanceof Element)) {
			return;
		}
		// if the element is not a custom element nor a custom built-in element there is nothing to
		// be done
		const name = (element.getAttribute('is') || element.tagName).toLowerCase();
		if (!name.includes('-')) {
			return;
		}
		// elements are tracked with a WeakMap to avoid to do the same work twice
		let componentEntry = this.components.get(element);
		if (!componentEntry) {
			const entry = this.getEntry(name);
			componentEntry = {
				autodefineTrigger: null,
				autoloadTrigger: null,
				register: null,
				registering: false,
				get: () => {
					if (registry.get(name)) {
						registry.upgrade(element);
						return element;
					}
					return registry.whenDefined(name).then(() => {
						registry.upgrade(element);
						return element;
					});
				},
			};
			this.components.set(element, componentEntry);
			if (entry) {
				// initialize autodefinition and autoloading
				this.autodefine(name, element, entry, componentEntry, force);
				this.autoload(element, entry, componentEntry);
			}
		}
		// when it is required to have a ready component, we force the registration ignoring
		if (force && !componentEntry.registering && componentEntry.register) {
			componentEntry.registering = true;
			componentEntry.register();
		}
		// wait for the element to be defined
		await componentEntry.get();
	}

	/**
	 * Schedules a check and clears any existing timeout before setting a new one. Avoid unnecessary
	 * work when there are multiple subsequent calls to define()
	 *
	 * @private
	 *
	 */
	scheduleCheck() {
		if (this.checkTimeout) {
			clearTimeout(this.checkTimeout);
		}
		this.checkTimeout = setTimeout(() => this.check(), 0);
	}

	/**
	 *  Checks for changes in the DOM and prepare elements accordingly
	 *
	 * @private
	 *
	 */
	check() {
		// when called after a define() we will search in the dom in case defined elements already
		// exists there
		for (const element of this.extractElements([document.body])) {
			this.prepareElement(element, false);
		}
		// initialize the internal MutationObserver if it has not already been done
		if (!this.domObserver) {
			this.domObserver = new MutationObserver((entries) => {
				for (const entry of entries) {
					for (const element of this.extractElements(
						[...entry.addedNodes].filter((node) => node instanceof Element)
					)) {
						this.prepareElement(element, false);
					}
				}
			});
			this.domObserver.observe(document.body, {
				childList: true,
				subtree: true,
			});
		}
	}

	/**
	 * Callback called by both autodefine and autoload IntersectionObservers. When an element is
	 * intersecting, it stops to observe it and call the corresponding element trigger that was
	 * prepared before.
	 *
	 * @private
	 *
	 * @param {string} observerType 'autodefine' or 'autoload'
	 * @param {IntersectionObserverEntry[]} entries the entries that triggered an observed change
	 * @param {IntersectionObserver} observer the observer itself
	 */
	onIntersect(observerType, entries, observer) {
		for (const entry of entries) {
			if (entry.isIntersecting) {
				const element = entry.target;
				// stop to observe the element at the first valid intersection
				observer.unobserve(element);
				const componentEntry = this.components.get(element);
				// call the trigger
				if (componentEntry) {
					const triggerName = observerType + 'Trigger';
					if (componentEntry[triggerName]) {
						componentEntry[triggerName]();
						componentEntry[triggerName] = null;
					}
				}
			}
		}
	}

	/**
	 * Extracts from a list of given nodes all elements that match the registered custom elements or
	 * custom built-in elements
	 *
	 * @private
	 *
	 * @param {Element[]} nodes array of elements that will be queried to find registered custom
	 * elements (including the elements themself)
	 * @returns {Element[]} the elements that match the registered custom elements or custom
	 * built-in elements
	 */
	extractElements(nodes) {
		return nodes.reduce(
			(res, node) =>
				res.concat(node.matches(this.selectors) ? [node] : [], [
					...node.querySelectorAll(this.selectors),
				]),
			[]
		);
	}

	/**
	 * Initialize the autodefinition of the element based on the global and eventually custom
	 * configuration
	 *
	 * @private
	 *
	 * @param {string} name custom element or custom built-in element name
	 * @param {Element} element the element to be eventually observed
	 * @param {object} entry config object eventually containing custom options for the specific
	 * definition
	 * @param {object} componentEntry internal entry used to monitor the specific element
	 * @param {boolean} force false when the element has been intercepted by the mutation
	 * observed, true in all the other cases in which the element has been manually queried or
	 * upgraded
	 */
	autodefine(name, element, entry, componentEntry, force) {
		// if the custom element is already defined there is nothing to be done
		if (!registry.get(name)) {
			// we let the custom options to eventually override the global ones
			const {autodefineAttribute, lazyValue, autodefineDefaultValue} = assign(
				{},
				this.options,
				entry
			);
			// any single element can override the autodefine behavior with a specific attribute (by
			// default a data attribute)
			const autodefine = element.hasAttribute(autodefineAttribute)
				? element.getAttribute(autodefineAttribute)
				: autodefineDefaultValue;
			// actual registration function, that wait for the lazy definition and double-check to
			// avoid to register the element twice due to async behavior
			componentEntry.register = async () => {
				if (!registry.get(name)) {
					const constructor = await this.get(name);
					if (!registry.get(name)) {
						if (element.hasAttribute('is')) {
							registry.define(name, constructor, {extends: element.tagName.toLowerCase()});
						} else {
							registry.define(name, constructor);
						}
					}
				}
			};
			// when the element is queried or upgraded manually or it declares an autodefine
			// attribute = 'true' we register it immediately
			if (force || autodefine === 'true') {
				componentEntry.registering = true;
				componentEntry.register();
			} else if (autodefine === lazyValue) {
				// otherwise we create a registration promise to be resolved lazily only when the
				// element intersects
				new Promise((autodefineTrigger) => {
					componentEntry.autodefineTrigger = autodefineTrigger;
				}).then(() => {
					componentEntry.registering = true;
					componentEntry.register();
				});
				// get a proper observer and start to observe the element
				this.getObserver('autodefine', entry).observe(element);
			}
		}
	}

	/**
	 * Initialize the autoloading of the element based on the global and eventually custom
	 * configuration and on the element attributes
	 *
	 * @private
	 *
	 * @param {Element} element the element to be eventually observed
	 * @param {object} entry config object eventually containing custom options for the specific
	 * definition
	 * @param {object} componentEntry internal entry used to monitor the specific element
	 */
	async autoload(element, entry, componentEntry) {
		// we let the custom options to eventually override the global ones
		const {autoloadAttribute, loadMethod, lazyValue} = assign({}, this.options, entry);
		const hasAutoload = element.hasAttribute(autoloadAttribute);
		const autoload = hasAutoload ? element.getAttribute(autoloadAttribute) : false;
		// only elements with a proper autoload attribute and with a value !== 'false' will be taken
		// into consideration for autoload behavior
		if (autoload && autoload !== 'false') {
			// the actual load call. It checks that the load method exists on the element before
			// calling it
			const load = () => {
				if (loadMethod in element && element[loadMethod] instanceof Function) {
					element[loadMethod]();
				}
			};
			// when the attribute value is 'true' we trigger the load immediately
			if (autoload === 'true') {
				await componentEntry.get();
				load();
			} else if (autoload === lazyValue) {
				// otherwise when the element requires an lazy load we define a load promise that is
				// going to be lazily resolved only when the element intersects
				const autoloadPromise = new Promise((autoloadTrigger) => {
					componentEntry.autoloadTrigger = autoloadTrigger;
				});
				// get a proper observer and start to observe the element
				this.getObserver('autoload', entry).observe(element);
				// autoload call will be performed only after the element is defined and upgraded
				await componentEntry.get();
				await autoloadPromise;
				load();
			}
		}
	}

	/**
	 * Prepare a constructor by ensuring it is the desired class, and eventually subclass to
	 * implement the firstConnectedCallback pattern
	 *
	 * @private
	 *
	 * @param {Function} constructor the function that generates a constructor or the constructor to
	 * be prepared
	 * @returns {Function} the constructor ready to be registered
	 */
	prepareConstructor(constructor, options) {
		if (options.extends) {
			// when the constructor was defined as a mixin in the form const ClassName = (Base)
			// => class extends Base {} it is still not yet a proper Element constructor, it
			// needs to be called passing the actual parent constructor
			const parentConstructor = getParentConstructor(options.extends);
			if (!(constructor.prototype instanceof parentConstructor)) {
				constructor = constructor(parentConstructor);
			}
		}
		// if enabled and the method is available in the given constructor, it subclass the
		// constructor to implement a firstConnectedCallback mechanism. That method is going to
		// be called only the first time (without calling connectedCallback(), while
		// connectedCallback() is going to be called all the next times)
		const firstConnectedCallbackMethod =
			'firstConnectedCallbackMethod' in options
				? options.firstConnectedCallbackMethod
				: this.options.firstConnectedCallbackMethod;
		if (
			firstConnectedCallbackMethod &&
			isString(firstConnectedCallbackMethod) &&
			firstConnectedCallbackMethod in constructor.prototype
		) {
			const hasConnectedCallback = 'connectedCallback' in constructor.prototype;
			constructor = class extends constructor {
				connectedCallback() {
					if (elementsConnectedOnce.has(this)) {
						if (hasConnectedCallback) {
							super.connectedCallback();
						}
					} else {
						elementsConnectedOnce.add(this);
						this[firstConnectedCallbackMethod]();
					}
				}
			};
		}
		return constructor;
	}

	/**
	 * Retrieves an entry for a custom element. In case of custom built-in element defined throught
	 * mixin in order to support multiple parents, it clones the entry for the specific requested
	 * parent only when it's needed
	 *
	 * @private
	 *
	 * @param {string} name the name of the defined custom element or custom built-in element
	 * @returns {(object|undefined)} an entry object or undefined when the definition does not exist
	 */
	getEntry(name) {
		let baseName = name;
		let tagName = '';
		let entry = this.entries.get(name);
		if (!entry) {
			// for definition of custom built-in elements with multiple possible parents the base
			// name is in the form 'acme-whatever-' and the specific elements name are obtained
			// appending the tagName to the base name
			const parts = name.split('-');
			if (parts.length > 2) {
				tagName = parts.pop();
				baseName = parts.join('-') + '-';
			}
			const baseEntry = this.entries.get(baseName);
			if (baseEntry) {
				if (baseEntry.extends === tagName || baseEntry.extends === '*') {
					entry = assign({}, baseEntry);
					entry.extends = tagName;
					this.entries.set(name, entry);
				}
			}
		}
		return entry;
	}

	/**
	 * Returns an IntersectionObserver object based on the given type and options. Observers get reused when the type
	 * and options have been already used to create one before, so only the necessary amount of them is created.
	 *
	 * @private
	 *
	 * @param {string} type 'autodefine' or 'autoload'
	 * @param {object} options additional options like threshold, root margins  @see {@link LazyCustomElementRegistry#setOptions}
	 * @returns {IntersectionObserver}
	 */
	getObserver(type, options) {
		// observers groups are stored in a WeakMap by their root (which can be a Document or an Element)
		const rootName = type + 'Root';
		const root = rootName in options ? options[rootName] : this.options[rootName];
		let map = this.observers.get(root);
		if (!map) {
			map = {};
			this.observers.set(root, map);
		}
		// observer name inside each group is defined by their type ('autodefine' or 'autoload'),
		// rootMargin and threshold
		const parts = [type];
		for (const suffix of ['RootMargin', 'Threshold']) {
			const propName = type + suffix;
			const prop = propName in options ? options[propName] : this.options[propName];
			parts.push(prop);
		}
		const observerName = parts.join('-');
		if (!map[observerName]) {
			map[observerName] = new IntersectionObserver(
				(entries, obs) => this.onIntersect(type, entries, obs),
				{
					root: root,
					rootMargin: parts[1], // margins
					threshold: parts[2], // threshold
				}
			);
		}
		return map[observerName];
	}
}

/**
 * A shared instance of LazyCustomElementRegistry
 * @instance
 * @type LazyCustomElementRegistry
 */
const lazyCustomElements = new LazyCustomElementRegistry();

//! nuff/events - @author Federico Orru' - @license Artistic-2.0

let lateEventsOrder = 0;
// Late events make sense only for objects that are still referenced in the
// code, so we store them in a WeakMap using the dispatcher as key, in order to
// not prevent their garbage collection.
const lateEvents = new WeakMap();
const hiddenTargets = new WeakMap();
const eventTargetsCache = new WeakMap();

/**
 * Associates an EventTarget to objects that are not instances of EventTarget on their own
 *
 * @private
 *
 * @param {object} target the object to map to an event target
 * @returns {EventTarget} the associated event target
 */
function getHiddenTarget(target) {
	let hiddenTarget = hiddenTargets.get(target);
	if (!hiddenTarget) {
		hiddenTarget = new EventTarget();
		hiddenTargets.set(target, hiddenTarget);
	}
	return hiddenTarget;
}

/**
 * Verifies that an object is an instance of EventTarget or at least implement the same interface
 *
 * @private
 *
 * @param {object} target the object to be verified
 * @returns {boolean}
 */
const isValidEventTarget = (target) =>
	('EventTarget' in window && target instanceof EventTarget) ||
	['addEventListener', 'removeEventListener', 'dispatchEvent'].filter((method) => method in target).length === 3;

/**
 * Checks if the value passed is a valid event listener, meaning it is a function or an object with a handleEvent() method
 *
 * @private
 *
 * @param {?(Function|object)} listener object to be verified
 * @returns {boolean}
 */
const isValidListener = (listener) => listener && (listener instanceof Function || ('handleEvent' in listener && listener.handleEvent instanceof Function));

/**
 * Sets up an EventListener
 *
 * @param {(object|EventTarget)} target an EventTarget (or any other JS object) to listen for the
 * event(s). Objects that are not natively EventTarget will be mapped internally to an EventTarget
 * in order to emulate the listening.
 * @param {(string|string[])} types a single event type or an array of event types or a
 * space-separated list of event type. Example: 'click', ['mouseenter', 'mouseleave'], 'resize
 * scroll'.
 * @param {(function|object)} [listener] a function or object that implement the handleEvent()
 * method
 * @param {(object|boolean)} [options] contains the listening options. Accepts all the standrd
 * options accepted by addEventListener - capture, once, passive, signal - plus three additional
 * custom properties used to provide additional - non standard - features.
 * @param {string} [options.selector] used to implement event delegation. The listener will be
 * called only when the event has been dispatched by elements nested in the target that match the
 * selector.
 * @param {(number|boolean)} [options.throttle] when throttle is true, the requestAnimationFrame
 * will be used to throttle the calls to the listeners. When it is a number, it will be used as
 * milliseconds mimimum interval between the calls. The throttled listener is guaranteed to be
 * called both with the first and the last event, while those in between may be ignored. The last
 * call could be delayed in respect to the actual event moment.
 * @param {boolean} [options.late] when the late flag is true, the listener can be called (and the
 * inner promise resolved) also if a 'late' event has been dispatched before setting up the
 * listener. A late event is a CustomEvent dispatched by the [dispatch() function]{@link dispatch}
 * setting the late option to true.
 * @returns {EventListener} an instance of EventListener configured with the given parameter
 */
function listen(target, types, listener, options) {
	return new EventListener(target, types, listener, options);
}

/**
 * Dispaches an event for the given target
 *
 * @param {object|EventTarget} target an EventTarget (or any other JS object) that will dispatch the
 * event. Objects that are not natively EventTarget will be mapped internally to an EventTarget in
 * order to emulate the dispatching.
 * @param {(string|Event)} type a type of a custom event to be created or an already created Event
 * object
 * @param {object} [options] can contain the standard options to create an event (details, bubbles,
 * cancelable, composed), plus an additional 'late' option
 * @param {boolean} [options.late=false] when passed and true, the event will be kept after the
 * dispatch in order to be notified also to "late listeners", that are event listeners created with
 * the late flag after the event has been already dispatched
 * @fires Event
 * @returns {Event} the dispatched event object
 */
function dispatch(target, type, options = {}) {
	const late = !!options.late;
	delete options.late;
	const event = type instanceof Event ? type : new CustomEvent(type, options);
	const eventTarget = isValidEventTarget(target) ? target : getHiddenTarget(target);
	if (late) {
		type = event.type;
		let entry = lateEvents.get(eventTarget);
		if (!entry) {
			entry = {};
			lateEvents.set(eventTarget, entry);
		}
		entry[type] = {order: lateEventsOrder++, event: event};
	}
	eventTarget.dispatchEvent(event);
	return event;
}

/**
 * EventListener objects are thenable objects that allows to remove the listener and get a promise
 * resolved after the event has been dispatched.
 *
 */
class EventListener {
	/**
	 * Creates an instance of EventListener.
	 *
	 * @param {(object|EventTarget)} target an EventTarget (or any other JS object) to listen for the
	 * event(s)
	 * @param {(string|string[])} types a single event type or an array of event types or a
	 * space-separated list of event type. Example: 'click', ['mouseenter', 'mouseleave'], 'resize
	 * scroll'.
	 * @param {(function|object)} [listener] a function or object that implement the handleEvent()
	 * method
	 * @param {(object|boolean)} [options] contains the listening options. Accepts all the standrd
	 * options accepted by addEventListener - capture, once, passive, signal - plus three additional
	 * custom properties used to provide additional - non standard - features.
	 * @param {string} [options.selector] used to implement event delegation. The listener will be
	 * called only when the event has been dispatched by elements nested in the target that match the
	 * selector.
	 * @param {(number|boolean)} [options.throttle] when throttle is true, the requestAnimationFrame
	 * will be used to throttle the calls to the listeners. When it is a number, it will be used as
	 * milliseconds mimimum interval between the calls. The throttled listener is guaranteed to be
	 * called both with the first and the last event, while those in between may be ignored. The last
	 * call could be delayed in respect to the actual event moment.
	 * @param {boolean} [options.late] when the late flag is true, the listener can be called (and the
	 * inner promise resolved) also if a 'late' event has been dispatched before setting up the
	 * listener. A late event is a CustomEvent dispatched by the [dispatch() function]{@link dispatch}
	 * setting the late option to true.
	 */
	constructor(target, types, listener, options) {
		// accept a list of event types both as an array or as a space-separated string
		if (!isArray(types)) {
			types = types.split(/\s+/);
		}

		// it could be called passing only 3 arguments in which the listener is omitted and the options
		// params shifts to the left
		if (!options && !isValidListener(listener)) {
			options = listener;
			listener = null;
		}

		// when the options is boolean it corresponds to setting the capture flag
		if (!options) {
			options = {capture: false};
		} else if (options === true) {
			options = {capture: true};
		}

		// it can listen to events from any kind of object. When the target is not a valid event target,
		// we map it to an hidden EventTarget
		const isEventTarget = isValidEventTarget(target);
		const eventTarget = isEventTarget ? target : getHiddenTarget(target);

		// selector option used to implement event delegation makes sense only if the target is an
		// Element or a Document
		const selector = target instanceof Element || target instanceof Document ? options.selector : null;
		const throttleValue = options.throttle;
		const late = !!options.late;

		// without a listener, we force the once flag, because the event would be notified only once
		// through the returned Promise-like object anyway
		if (!listener) {
			options.once = true;
		}
		this.once = !!options.once;

		// we remove the custom properties from the options, in order to leave only the standard options
		// to be passed to the native addEventListener call
		delete options.selector;
		delete options.throttle;
		delete options.late;
		if (!keys(options).length) {
			options = false;
		}

		// we prepare the promise to be resolved after the first event is intercepted
		let triggerResolve;
		const promise = new Promise((resolve) => {
			triggerResolve = resolve;
		});

		// we will store a different throttled handler for each listened event type
		const throttled = {};
		this.done = false;
		let resolved = false;

		// internal handler, calling the provided listener and resolving the promise
		const handle = (event) => {
			let delegateTarget = eventTarget;
			// when a selector was specified, we consider only events originated by the matching
			// elements and their descendants
			if (selector) {
				const closestMatch = event.target.closest(selector);
				delegateTarget = closestMatch && eventTarget.contains(closestMatch) ? closestMatch : null;
			}
			if (delegateTarget) {
				const targetParam = isEventTarget ? delegateTarget : target;
				if (listener && !(this.once && this.done)) {
					if ('handleEvent' in listener && listener.handleEvent instanceof Function) {
						listener.handleEvent(event, targetParam);
					} else {
						listener(event, targetParam);
					}
				}
				this.done = true;
				if (!resolved) {
					resolved = true;
					triggerResolve(event);
				}
			}
		};

		// the actual handler being registered, which takes care of throttling the internal handle
		// when requested
		const handler = (event) => {
			// we have to throttle a separate function for each listened event type
			const type = event.type;
			if (throttleValue) {
				if (!(type in throttled)) {
					throttled[type] = throttle(handle, throttleValue);
				}
				throttled[type](event);
			} else {
				handle(event);
			}
		};

		// if requesting for late events, we check immediately if the events already fired before
		// the call
		if (late) {
			// events could have been dispatched by the target itself or by any descendants
			let lateTargets = [eventTarget];
			if (target === eventTarget && 'querySelectorAll' in target) {
				lateTargets = lateTargets.concat([...target.querySelectorAll(selector ? selector : '*')]);
			}
			const pendingEvents = [];
			for (const lateTarget of lateTargets) {
				const entries = lateEvents.get(lateTarget);
				if (entries) {
					for (const type of types) {
						if (type in entries) {
							pendingEvents.push(entries[type]);
							break;
						}
					}
				}
			}
			// when there are multiple pending events we call the handler only with the last one
			if (pendingEvents.length) {
				pendingEvents.sort((a, b) => b.order - a.order);
				handle(pendingEvents[0].event);
			}
		}

		// we register an actual native listener only if the listening has not been already
		// fulfilled by previously dispatched late events
		if (!this.once || !this.done) {
			types.map((type) => eventTarget.addEventListener(type, handler, options));
		}

		// make vars accessible by other methods
		this.promise = promise;
		this.options = options;
		this.types = types;
		this.handler = handler;
		this.eventTarget = eventTarget;
	}

	/**
	 * Return a promise that resolves when the first listened event has been dispatched.
	 *
	 * @param {Function} [callback] optional callback to be called after the promise's resolution.
	 * @returns {Promise<Event>} the returned promise is resolved with the Event object.
	 */
	then(callback) {
		return this.promise.then(callback);
	}

	/**
	 * Remove the event(s) listener(s).
	 *
	 */
	unlisten() {
		if (!this.once || !this.done) {
			this.types.map((type) => this.eventTarget.removeEventListener(type, this.handler, this.options));
		}
	}
}

/**
 * Object that wraps another object (the event target) in order to call listen() or dispatch() event
 * without repeating the target every time.
 * May be practical if you needs to perform several event-related calls.
 */
class WrappedEventTarget {
	/**
	 * Creates an instance of WrappedEventTarget.
	 * @param {(object|EventTarget)} target an EventTarget (or any other JS object) that can
	 * dispatch events and can to be listened for events.
	 */
	constructor(target) {
		this.target = target;
	}

	/**
	 * Attaches an event listener to the target.
	 * @see [listen() function]{@link listen} for details about the parameters and the return value
	 *
	 */
	listen(types, listener, options) {
		return new EventListener(this.target, types, listener, options);
	}

	/**
	 * Dispatches an event.
	 * @see [dispatch() function]{@link dispatch} for details about the parameters and the return
	 * value
	 *
	 */
	dispatch(type, options) {
		return dispatch(this.target, type, options);
	}
}

/**
 * Gives back a weakly cached WrappedEventTarget
 *
 * @param {(object|EventTarget)} target the object to be wrapped
 * @returns {WrappedEventTarget} the WrappedEventTarget associated with the given target
 */
function eventTarget(target) {
	let wrapped = eventTargetsCache.get(target);
	if (!wrapped) {
		wrapped = new WrappedEventTarget(target);
		eventTargetsCache.set(wrapped);
	}
	return wrapped;
}

//! nuff/classlist - @author Federico Orru' - @license Artistic-2.0

const cache$2 = {};

let globalNs$1 = '';

const mapNs = (classes, ns) => classes.map((name) => ns + name);

/**
 * It provides methods for manipulating and accessing the classList of an Element, with support for
 * namespaces. API almost completely equivalent to Element.classList.
 *
 * @see [DOMTokenList]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList}
 *
 */
class ClassList {
	/**
	 * Creates an instance of ClassList.
	 *
	 * @param {Element} element the element for which we are creating the instance
	 * @param {string} [ns] optional namespace. If specified, it will be put at the beginning of
	 * every class name set through this class. If not specified, a global one previously set will
	 * be used.
	 */
	constructor(element, ns) {
		ns = ns === undefined ? globalNs$1 : ns;
		this.element = element;
		this.ns = ns;
		this.cl = element.classList;
	}

	/**
	 * Getter equivalent to Element.classList.length, but taking the namespace into account. Gets
	 * the number of class names that start with the specified namespace.
	 * @see [DOMTokenList:
	 * length]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/length}
	 *
	 * @readonly
	 * @type {number}
	 */
	get length() {
		return this.values().length;
	}

	/**
	 * Getter / Setter equivalent to Element.classList.value, but taking the namespace into account.
	 * @see [DOMTokenList:
	 * value]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/value}
	 *
	 * @type {string}
	 * @memberof ClassList
	 */
	get value() {
		return this.values().join(' ');
	}

	set value(classes) {
		this.cl.value = mapNs(classes.split(/\s+/g), this.ns).join(' ');
	}

	/**
	 * Equivalent to Element.classList.add(), but taking the namespace into account.
	 * @see [DOMTokenList:
	 * add()]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/add}
	 *
	 * @example <caption>Adding classes</caption>
	 * ``` javascript
	 * import {classList} from 'nuff';
	 * classList.cs = 'acme-';
	 * const element = document.createElement('div');
	 * const cs = classList(element);
	 * cs.add('test'); // adds 'acme-test'
	 * cs.add('one', 'two', 'three'); // adds 'acme-one', 'acme-two', 'acme-three'
	 * ```
	 *
	 * @param {...string} classes one or more class names
	 */
	add(...classes) {
		this.cl.add(...mapNs(classes, this.ns));
	}

	/**
	 * Equivalent to Element.classList.remove(), but taking the namespace into account.
	 * @see [DOMTokenList:
	 * remove()]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/remove}
	 *
	 * @param {...string} classes one or more class names
	 */
	remove(...classes) {
		this.cl.remove(...mapNs(classes, this.ns));
	}

	/**
	 * Equivalent to Element.classList.contains(), but taking the namespace into account.
	 * @see [DOMTokenList:
	 * contains()]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/contains}
	 *
	 * @param {string} name the class name to check
	 * @returns {boolean} true if the given class name is set on the element, false otherwise
	 */
	contains(name) {
		return this.cl.contains(this.ns + name);
	}

	/**
	 * Equivalent to Element.classList.toggle(), but taking the namespace into account.
	 * @see [DOMTokenList:
	 * toggle()]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/toggle}
	 *
	 * @param {string} name the class name to be toggled
	 * @param {boolean} [force] a flag to force the add or remove of the class name
	 * @returns {boolean} true when the class name is present after the call, false otherwise
	 */
	toggle(name, force) {
		return this.cl.toggle(this.ns + name, force);
	}

	/**
	 * Equivalent to Element.classList.replace(), but taking the namespace into account.
	 * @see [DOMTokenList:
	 * replace()]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/replace}
	 *
	 * @param {string} oldClass the class name to be replaced
	 * @param {string} newClass the class name replacement
	 * @returns {boolean} true if oldToken was successfully replaced, or false if not.
	 */
	replace(oldClass, newClass) {
		return this.cl.replace(this.ns + oldClass, this.ns + newClass);
	}

	/**
	 * Equivalent to Element.classList.values(), but taking the namespace into account.
	 * @see [DOMTokenList:
	 * values()]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/values}
	 *
	 * @returns {array} the iterable array of class names that match with the namespace
	 */
	values() {
		return [...this.cl.values()]
			.filter(
				(name) => !this.ns.length || (name.length > this.ns.length && name.indexOf(this.ns) === 0)
			)
			.map((name) => name.substring(this.ns.length));
	}

	/**
	 * Equivalent to Element.classList.keys(), but taking the namespace into account.
	 * @see [DOMTokenList:
	 * keys()]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/keys}
	 *
	 * @returns {array} the list of indexes, based on the class names list that match with the
	 * namespace
	 */
	keys() {
		return this.values().map((name, index) => index);
	}

	/**
	 * Equivalent to Element.classList.entries(), but taking the namespace into account.
	 * @see [DOMTokenList:
	 * entries()]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/entries}
	 *
	 * @returns {array} the iterable list of [index, class name] built with class names that match
	 * the namespace
	 */
	entries() {
		return this.values().map((name, index) => [index, name]);
	}

	/**
	 * Equivalent to Element.classList.item(), but taking the namespace into account.
	 * @see [DOMTokenList:
	 * item()]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/item}
	 *
	 * @param {number} index the index of the class name, taking into account only those that
	 * match with the namespace
	 * @returns {?string} the class name at the specified index or null when the index is out of
	 * range
	 */
	item(index) {
		const values = this.values();
		return index >= 0 && index < values.length ? values[index] : null;
	}

	/**
	 * Equivalent to Element.classList.forEach(), but taking the namespace into account.
	 * @see [DOMTokenList:
	 * forEach()]{@link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/forEach}
	 *
	 * @param {Function} callback The function to execute for each element
	 * @param {object} [context] The value to use as *this* when executing callback
	 */
	forEach(callback, context) {
		context = context || window;
		const values = this.values();
		values.map((name, i) => callback.call(context, name, i, values));
	}
}

/**
 * Returns a ClassList object for a given element and namespace, using a cache to avoid unnecessary
 * object creation (when called with the same element and namespace, it will always return the same
 * object).
 *
 * @param {Element} element the element for which we are creating the instance
 * @param {string} [ns] optional namespace. If specified, it will be put at the beginning of
 * every class name set through the class. If not specified, a global one previously set will
 * be used.
 * @returns {ClassList} a ClassList object for the given element and namespace
 */
function classList(element, ns) {
	ns = ns === undefined ? globalNs$1 : ns;
	if (!(ns in cache$2)) {
		cache$2[ns] = new WeakMap();
	}
	let cs = cache$2[ns].get(element);
	if (!cs) {
		cs = new ClassList(element, ns);
		cache$2[ns].set(element, cs);
	}
	return cs;
}

/**
 * Gets the global namespace
 *
 * @returns {string}
 * @memberof classList
 */
classList.getNs = () => globalNs$1;

/**
 * Sets the global namespace
 *
 * @param {string} value the new namespace
 * @memberof classList
 */
classList.setNs = (value) => {
	globalNs$1 = String(value);
};

/**
 * Returns a CSS selector for the given class names, adding the namespace to them
 *
 * @param {(string|Array)} name class name or array of class names
 * @param {string} [ns] optional namespace. If specified, it will be put at the beginning of every
 * class name. If not specified, a global one previously set will be used.
 * @returns {string} the generated CSS selector
 * @memberof classList
 */
classList.selector = (name, ns) => {
	ns = ns === undefined ? globalNs$1 : ns;
	return (Array.isArray(name) ? name : name.split(/\s+/g)).map((n) => '.' + ns + n).join('');
};

/**
 * Returns a class name after adding the namespace to it.
 *
 * @param {string} name class name
 * @param {string} [ns] optional namespace. If specified, it will be put at the beginning of the
 * class name. If not specified, a global one previously set will be used.
 * @returns {string} the class name starting with the namespace
 * @memberof classList
 */
classList.className = (name, ns) => {
	ns = ns === undefined ? globalNs$1 : ns;
	return ns + name;
};

//! nuff/dataset - @author Federico Orru' - @license Artistic-2.0

const attrNames = {};
const propNames = {};

const cache$1 = {};

let globalNs = '';

const fullName = (name, ns) =>
	ns.length ? ns + name.substring(0, 1).toUpperCase() + name.substring(1) : name;

const fixNs = (ns) =>
	ns.length && ns.charAt(ns.length - 1) === '-' ? ns.substring(0, ns.length - 1) : ns;

function getAttrName(name, ns) {
	name = fullName(name, ns);
	return (attrNames[name] =
		attrNames[name] || 'data-' + name.replace(/([A-Z])/g, '-$1').toLowerCase());
}

function getPropName(name, ns) {
	return (propNames[name] =
		propNames[name] ||
		name
			.substring(ns ? getAttrName(ns, '').length + 1 : 5)
			.replace(/-([a-z])/g, (all, letter) => letter.toUpperCase()));
}

function escape(value) {
	value = JSON.stringify([value]);
	return value.substring(2, 2 + value.length - 4);
}

/**
 * Dataset objects allow to do typical CRUD operations on an element's data attributes, with support
 * for a namespace
 *
 */
class Dataset {
	/**
	 * Creates an instance of Dataset
	 *
	 * @param {Element} element the element for which we are creating the instance
	 * @param {string} [ns] optional namespace. If specified, it will be put at the beginning of
	 * every class name set through this class. If not specified, a global one previously set will
	 * be used.
	 */
	constructor(element, ns) {
		ns = ns === undefined ? globalNs : fixNs(ns);
		this.element = element;
		this.ns = ns;
	}

	/**
	 * Gives true if the data attribute with the given name (after applying an eventual namespace)
	 * exists on the element
	 *
	 * @param {string} name the name of the attribute. It accept names expressed also in
	 * camelCase, it will internally be converted to proper data attribute name.
	 * @returns {boolean}
	 */
	has(name) {
		return this.element.hasAttribute(getAttrName(name, this.ns));
	}

	/**
	 * Retrieve a data attribute value or all of them as long as they match the previously set
	 * namespace. Returned values are casted to the proper type among boolean, numbers, array and
	 * object (when they are encoded as JSON)
	 *
	 * @param {(string|object)} [name] When a string value is passed, it returns the the value of
	 * that attribute after applying the namespace. Name passed in camelCase are supported. If the
	 * attribute is not set it returns the eventual fallbackValue passed as a second parameter, or
	 * null otherwise. When no value is passed, it returns all data attributes values that match
	 * with the set namespace. When a plain object is passed, it is used as a set of default values:
	 * in will return all the values that match the namespace when they are available or the given
	 * default values
	 * @param {*} [fallbackValue] When a string name is passed and that attribute is not present,
	 * this fallbackValue will be returned
	 * @returns {(string|number|boolean|Array|object|null)} the retrieved value or values after
	 * casting them in the proper type or the fallbackValue
	 */
	get(name, fallbackValue) {
		const c = this.constructor;
		if (isString(name) && name.length) {
			return c.decode(this.getString(name, fallbackValue));
		}
		const values = this.getString();
		for (const [key, value] of entries(values)) {
			values[key] = c.decode(value);
		}
		return seemsPlainObject(name) ? assign({}, name, values) : values;
	}

	/**
	 * Exactly like the [get() method]{@link Dataset#get}, but without casting the value types, they
	 * will be returned as string
	 *
	 * @param {(string|object)} [name] @see [get() method]{@link Dataset#get}
	 * @param {*} [fallbackValue] [get() method]{@link Dataset#get}
	 * @returns {(string|object|null)} the retrieved value or values as strings
	 * or the fallbackValue.
	 */
	getString(name, fallbackValue) {
		if (isString(name) && name.length) {
			let value = this.element.getAttribute(getAttrName(name, this.ns));
			if (value === null && fallbackValue !== undefined) {
				value = fallbackValue;
			}
			return value;
		}
		const prefix = 'data-' + this.ns;
		const values = [...this.element.attributes].reduce((obj, attr) => {
			if (attr.name.indexOf(prefix) === 0) {
				obj[getPropName(attr.name, this.ns)] = attr.value;
			}
			return obj;
		}, {});
		return seemsPlainObject(name) ? assign({}, name, values) : values;
	}

	/**
	 * Sets one or more data attributes values, after applying the namespace to their name. Plain
	 * objects and arrays are stored in their JSON representation
	 *
	 * @param {(string|object)} name the name of the data attribute to be set or a plain object
	 * containing multiple name-value entries to be set. The names can be expressed in camelCase and
	 * the eventual namespace will be added before setting them
	 * @param {?(string|number|boolean|Array|object|null)} [value] The value to be set when the name is
	 * a string
	 */
	set(name, value) {
		const setOne = (n, v) => {
			n = getAttrName(n, this.ns);
			if (v !== undefined && v !== null) {
				this.element.setAttribute(n, this.constructor.encode(v));
			} else {
				this.element.removeAttribute(n);
			}
		};

		if (isString(name)) {
			setOne(name, value);
		} else {
			entries(name).map(([attr, val]) => setOne(attr, val));
		}
	}

	/**
	 * Remove a data attribute, after applying the eventual namespace to the name
	 *
	 * @param {string} name the name of the attribute to be removed. Can be expressed in camelCase
	 * format.
	 */
	remove(name) {
		this.element.removeAttribute(getAttrName(name, this.ns));
	}

	/**
	 * Cast values to strings. Used before storing the values in the data attributes
	 *
	 * @static
	 * @param {(string|number|boolean|Array|object|null)} value the value to be cast
	 * @returns {string} the string representation of the given value
	 */
	static encode(value) {
		return String(encode(value));
	}

	/**
	 * Try to cast a value expressed as string to a more suitable standard type
	 *
	 * @static
	 * @param {string} value the string value to be converted
	 * @returns {(string|number|boolean|Array|object|null)} The value after the cast
	 */
	static decode(value) {
		return decode(value);
	}
}

/**
 * Returns a Dataset object for a given element and namespace, using a cache to avoid unnecessary
 * object creation (when called with the same element and namespace, it will always return the same
 * object)
 *
 * @param {Element} element the element for which we are creating the instance
 * @param {string} [ns] optional namespace. If specified, it will be put at the beginning of every
 * data attribute name set through the class. If not specified, a global one previously set will be
 * used.
 * @returns {Dataset} a Dataset object for the given element and namespace
 */
function dataset(element, ns) {
	ns = ns === undefined ? globalNs : fixNs(ns);

	if (!(ns in cache$1)) {
		cache$1[ns] = new WeakMap();
	}
	let ds = cache$1[ns].get(element);
	if (!ds) {
		ds = new Dataset(element, ns);
		cache$1[ns].set(element, ds);
	}
	return ds;
}

/**
 * Gets the global namespace
 *
 * @returns {string}
 * @memberof dataset
 */
dataset.getNs = () => globalNs;

/**
 * Sets the global namespace
 *
 * @param {string} value the new namespace
 * @memberof dataset
 */
dataset.setNs = (value) => {
	globalNs = fixNs(value);
};

/**
 * Returns a CSS selector in the form [data-ns-name] or [data-ns-name="value"] for the given
 * attribute name and value, taking into account the namespace.
 *
 * @param {string} name data attribute name for the selector. It accepts also names in camelCase
 * @param {?(string|number|boolean|Array|object|null)} [value] the optional value to be used in
 * the selector, to generate a form . It gets converted to string
 * @param {string} [ns] optional namespace. If specified, it will be put at the beginning of the
 * data attribute name class name. If not specified, a global one previously set will be used.
 * @returns {string} the generated CSS selector
 * @memberof dataset
 */
dataset.selector = (name, value, ns) => {
	ns = ns === undefined ? globalNs : fixNs(ns);
	return (
		'[' +
		getAttrName(name, ns) +
		(value !== undefined ? '="' + escape(Dataset.encode(value)) + '"' : '') +
		']'
	);
};

/**
 * Returns a data attribute name for the given name, taking into account the namespace.
 *
 * @param {string} name accepts also names in camelCase
 * @param {?(string|number|boolean|Array|object|null)} [value] the optional value to be used in
 * the selector, to generate a form . It gets converted to string.
 * @param {string} [ns] optional namespace. If specified, it will be put at the beginning of the
 * data attribute name. If not specified, a global one previously set will be used.
 * @returns {string} the generated attribute name in the form data-ns-name
 * @memberof dataset
 */
dataset.attrName = (name, ns) => {
	return getAttrName(name, ns === undefined ? globalNs : fixNs(ns));
};

//! nuff / cssVars - @author Federico Orru' - @license Artistic License 2.0

const varNames = {};
const cache = {};

const getVarName = (name) => (varNames[name] = varNames[name] || (name.indexOf('--') === 0 ? name : '--' + name));

/**
 * CssVars objects allow to read / write CSS variables (custom properties) for the given element
 * (and eventual pseudo-element)
 *
 */
class CssVars {
	/**
	 * Creates an instance of CssVars.
	 *
	 * @param {Element} element the element for which we are creating the instance
	 * @param {?string} [pseudoElement] the optional pseudo-element. Examples: ':before',
	 * ':after', ':marker'
	 */
	constructor(element, pseudoElement) {
		this.element = element;
		this.pseudoElement = pseudoElement;
		this.computedStyle = getComputedStyle(element, pseudoElement);
		this.style = element.style;
	}

	/**
	 * Checkes if the given variable name has been set for the given element. A variable is
	 * considered to be set when the value is different from an empty string
	 *
	 * @param {string} name the name of the variable to be checked. It does not require the '--'
	 * at the beginning.
	 * @returns {boolean}
	 */
	has(name) {
		return this.fetch(name) !== '';
	}

	/**
	 * Gets the value of a variable (cast to a suitable standard type) or the given fallbackValue
	 * (or an empty string) when the variable is not set
	 *
	 * @param {string} name the name of the variable to be read. It does not require the '--' at
	 * the beginning.
	 * @param {?(string|number|boolean|Array|object|null)} [fallbackValue] when the variable is
	 * not set this fallbackValue will be returned
	 * @returns {(string|number|boolean|Array|object|null)} the variable value or the fallbackValue
	 * or an empty string when the variable is not set
	 */
	get(name, fallbackValue) {
		return this.constructor.decode(this.getString(name, fallbackValue));
	}

	/**
	 * Exactly like the [get() method]{@link CssVars#get}, but without casting the value type, it
	 * will be returned as string
	 *
	 * @param {string} name the name of the variable to be read. It does not require the '--' at
	 * the beginning.
	 * @param {?string} [fallbackValue] when the variable is not set this fallbackValue will be
	 * returned
	 * @returns {string} the variable value as a string or the fallbackValue or an empty string when
	 * the variable is not set
	 */
	getString(name, fallbackValue) {
		let value = this.fetch(name);
		if (value === '' && fallbackValue !== undefined) {
			value = fallbackValue;
		}
		return value;
	}

	/**
	 * Sets one or more CSS variables. Plain objects and arrays are stored in their JSON
	 * representation
	 *
	 * @param {(string|object)} name the name of the variable to be set or a plain object
	 * containing multiple name-value entries to be set. The names can be expressed without the
	 * '--', it will be added automatically.
	 * @param {?(string|number|boolean|Array|object|null)} [value] The value to be set when the
	 * name is a string
	 */
	set(name, value) {
		const setOne = (n, v) => this.style.setProperty(getVarName(n), this.constructor.encode(v));
		if (isString(name)) {
			setOne(name, value);
		} else {
			entries(name).map(([varName, val]) => setOne(varName, val));
		}
	}

	/**
	 * Removes a variable previously set with JS or in the inline style attribute. It cannot remove
	 * a variable set by a separated CSS file or by a <style> element.
	 *
	 * @param {string} name the name of the variable to be removed. It does not require the '--'
	 * at the beginning.
	 */
	remove(name) {
		this.style.removeProperty(getVarName(name));
	}

	/**
	 * Internal method to retrieve the variable value. When the element is connected to a document
	 * or a pseudo-element is specified, it tries to use the the element's computed style, otherwise
	 * it fallbacks to the element's style object. It automatically removes leading and trailing
	 * single and double quote chars for values that were expressed in that way.
	 *
	 * @private
	 *
	 * @param {string} name the name of the variable to be read. It does not require the '--'
	 * @returns {string} the variable value or an empty string when the variable is not found
	 */
	fetch(name) {
		return (this.element.isConnected || this.pseudoElement ? this.computedStyle : this.style)
			.getPropertyValue(getVarName(name))
			.trim()
			.replace(/^'(.*)'$/gi, '$1')
			.replace(/^"(.*)"$/gi, '$1');
	}

	/**
	 * Cast values to strings. Used before storing the values in the CSS variable
	 *
	 * @static
	 * @param {(string|number|boolean|Array|object|null)} value the value to be cast
	 * @returns {string} the string representation of the given value
	 */
	static encode(value) {
		return encode(value);
	}

	/**
	 * Try to cast a value expressed as string to a more suitable standard type
	 *
	 * @static
	 * @param {string} value the string value to be converted
	 * @returns {(string|number|boolean|Array|object|null)} The value after the cast
	 */
	static decode(value) {
		return decode(value);
	}
}

/**
 * Returns a CssVars object for a given element and optional pseudo-element, using a cache to avoid
 * unnecessary object creation (when called with the same element and pseudo-element, it will always
 * return the same object)
 *
 * @param {Element} element the element for which we are creating the instance
 * @param {?string} [pseudoElement] the optional pseudo-element. Examples: ':before', ':after',
 * ':marker'
 * @returns {CssVars} a CssVars object for the given element and pseudo-element
 */
function cssVars(element, pseudoElement = null) {
	const pseudo = pseudoElement === null ? '' : pseudoElement;
	if (!(pseudo in cache)) {
		cache[pseudo] = new WeakMap();
	}
	let entry = cache[pseudo].get(element);
	if (!entry) {
		entry = new CssVars(element, pseudoElement);
		cache[pseudo].set(element, entry);
	}
	return entry;
}

export { ClassList, CssVars, Dataset, EventListener, LazyCustomElementRegistry, WrappedEventTarget, classList, cssVars, dataset, dispatch, eventTarget, lazyCustomElements, listen };
//# sourceMappingURL=nuff.es.js.map
